import { useEffect, useState, useContext } from "react";
import axios from "axios";
import DynamicTable from "../../../Components/DynamicTable";
import UserContext from "../../../UserContext";
import AddBrandStaff from "./AddBrandStaff";
import {
  Box,
  Chip,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Autocomplete,
  Stack,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as XIcon,
} from "@mui/icons-material";
import SlidingSidebar, {
  ButtonControls,
} from "../../../Components/SlidingSidebar";

// Types moved to separate types.ts file
interface Staff {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  tags?: string[];
  role_data_json: {
    brand: string;
    markets: string[];
    brand_id: number;
    avatar_url: string;
    supplier_id: number;
  }[];
  user_access?: {
    tags?: string[];
    user_status?: {
      Verified?: string;
      Admin?: string;
      Active?: string;
    };
  };
}

interface StaffData {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  markets: string[];
  tags: string[];
  role_data_json: {
    user_status?: {
      Verified?: string;
      Admin?: string;
      Active?: string;
    };
  };
}

interface MarketOption {
  id: number;
  state: string;
  state_code: string;
}

interface TagOption {
  id: number;
  name: string;
}

interface SwitchStates {
  [key: string]: {
    Verified: boolean;
    Admin: boolean;
  };
}

// Separate component for table columns configuration
const useTableColumns = (switchStates: SwitchStates) => {
  return [
    {
      header: "First Name",
      dataIndex: "first_name",
      key: "first_name",
    },
    {
      header: "Last Name",
      dataIndex: "last_name",
      key: "last_name",
    },
    {
      header: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      header: "Markets",
      dataIndex: "markets",
      key: "markets",
      render: (markets: string[]) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {markets.map((market) => (
            <Chip key={market} label={market} size="small" />
          ))}
        </Box>
      ),
    },
    {
      header: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags: string[]) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      ),
    },
    {
      header: "Verified",
      dataIndex: "role_data_json",
      key: "verified",
      align: "center" as const,
      render: (_: any, record: StaffData) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {switchStates[record.user_id]?.Verified ? (
            <CheckCircleIcon sx={{ color: "primary.main" }} />
          ) : (
            <XIcon sx={{ color: "text.disabled" }} />
          )}
        </Box>
      ),
    },
    {
      header: "Admin",
      dataIndex: "role_data_json",
      key: "admin",
      align: "center" as const,
      render: (_: any, record: StaffData) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {switchStates[record.user_id]?.Admin ? (
            <CheckCircleIcon sx={{ color: "primary.main" }} />
          ) : (
            <XIcon sx={{ color: "text.disabled" }} />
          )}
        </Box>
      ),
    },
  ];
};

// Separate component for the edit form
const EditForm = ({
  editData,
  handleEditChange,
  availableMarkets,
  availableTags,
  switchStates,
  handleToggle,
}: any) => (
  <Stack spacing={0.5} sx={{ p: 2 }}>
    <Paper elevation={0} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Basic Information</Typography>
        <TextField
          label="First Name"
          size="small"
          value={editData.first_name}
          onChange={(e) => handleEditChange("first_name", e.target.value)}
          fullWidth
        />
        <TextField
          label="Last Name"
          size="small"
          value={editData.last_name}
          onChange={(e) => handleEditChange("last_name", e.target.value)}
          fullWidth
        />
        <TextField
          label="Email"
          size="small"
          value={editData.email}
          onChange={(e) => handleEditChange("email", e.target.value)}
          fullWidth
        />
      </Stack>
    </Paper>

    {/* Markets Section */}
    <Paper elevation={0} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Markets</Typography>
        <Autocomplete
          multiple
          options={availableMarkets}
          getOptionLabel={(option: MarketOption) => option.state}
          value={availableMarkets.filter((m: MarketOption) =>
            editData.markets.includes(m.state_code)
          )}
          onChange={(_, newValue) => {
            handleEditChange(
              "markets",
              newValue.map((v: MarketOption) => v.state_code)
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Markets" size="small" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...otherProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.state}
                  {...otherProps}
                  size="small"
                />
              );
            })
          }
        />
      </Stack>
    </Paper>

    {/* Tags Section */}
    <Paper elevation={0} sx={{ p: 1 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Tags</Typography>
        <Autocomplete
          multiple
          options={availableTags}
          getOptionLabel={(option: TagOption) => option.name}
          value={availableTags.filter((t: TagOption) =>
            editData.tags.includes(t.name)
          )}
          onChange={(_, newValue) => {
            handleEditChange(
              "tags",
              newValue.map((v: TagOption) => v.name)
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Tags" size="small" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...otherProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.name}
                  {...otherProps}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              );
            })
          }
        />
      </Stack>
    </Paper>

    {/* Permissions Section */}
    <Paper elevation={0} sx={{ p: 1 }}>
      <Stack spacing={1}>
        <Typography variant="h6">Permissions</Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography>Verified</Typography>
          <Switch
            checked={switchStates[editData.user_id]?.Verified || false}
            onChange={() => handleToggle(editData.user_id, "Verified")}
            color="primary"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography>Admin</Typography>
          <Switch
            checked={switchStates[editData.user_id]?.Admin || false}
            onChange={() => handleToggle(editData.user_id, "Admin")}
            color="primary"
          />
        </Box>
      </Stack>
    </Paper>
  </Stack>
);

const BrandStaff = () => {
  // State
  const [data, setData] = useState<StaffData[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editData, setEditData] = useState<StaffData | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [availableMarkets, setAvailableMarkets] = useState<MarketOption[]>([]);
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [switchStates, setSwitchStates] = useState<SwitchStates>({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { selectedBrandId } = useContext(UserContext) || {};

  // API Calls
  const fetchStaff = async () => {
    if (!selectedBrandId) return;

    try {
      const response = await axios.get<Staff[]>(
        `${import.meta.env.VITE_API_URL}/brands/staff-members`,
        { params: { brand_id: selectedBrandId } }
      );

      const filteredData = response.data
        .filter((member) => member.user_access?.user_status?.Active === "Yes") // Filter for active users
        .map((member: Staff) => {
          // Extract account status with defaults
          const accountStatus = {
            Verified: member.user_access?.user_status?.Verified || "No",
            Admin: member.user_access?.user_status?.Admin || "No",
            Active: member.user_access?.user_status?.Active || "No",
          };

          return {
            user_id: member.user_id,
            first_name: member.first_name,
            last_name: member.last_name,
            email: member.email,
            role: member.role,
            markets:
              member.role_data_json?.find(
                (role) => role.brand_id === selectedBrandId
              )?.markets || [],
            tags: member.user_access?.tags || [],
            role_data_json: {
              user_status: accountStatus,
            },
          };
        });

      setData(filteredData);

      // Initialize switch states
      const initialStates = filteredData.reduce((acc, member) => {
        acc[member.user_id] = {
          Verified: member.role_data_json.user_status.Verified === "Yes",
          Admin: member.role_data_json.user_status.Admin === "Yes",
        };
        return acc;
      }, {} as SwitchStates);

      setSwitchStates(initialStates);
    } catch (error) {
      console.error("Error fetching Staff members:", error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/settings/util-data?tables=usa_states,tags`
      );
      setAvailableMarkets(response.data.usa_states || []);
      setAvailableTags(response.data.tags || []);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  // Handlers
  const handleEdit = (record: StaffData) => {
    setEditingRow(record.user_id);
    setEditData({ ...record });
    setSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setEditingRow(null);
    setEditData(null);
  };

  const handleSave = async () => {
    if (!editData) return;

    try {
      const updateData = {
        user_id: editData.user_id,
        first_name: editData.first_name,
        last_name: editData.last_name,
        email: editData.email,
        user_access: {
          tags: editData.tags,
          user_status: {
            Verified: switchStates[editData.user_id]?.Verified ? "Yes" : "No",
            Admin: switchStates[editData.user_id]?.Admin ? "Yes" : "No",
            Active: "Yes",
          },
        },
        markets: editData.markets,
        brand_id: selectedBrandId,
        access_type: editData.role,
      };

      await axios.put(
        `${import.meta.env.VITE_API_URL}/users/update`,
        updateData
      );

      setData(
        data.map((member) =>
          member.user_id === editingRow
            ? {
                ...editData,
                role_data_json: {
                  user_status: updateData.user_access.user_status,
                },
              }
            : member
        )
      );

      // Show success message
      setSnackbarOpen(true);
      handleCloseSidebar();
    } catch (error: any) {
      console.error("Error updating user:", error);
      console.error("Error details:", error.response?.data);
    }
  };

  const handleDelete = async (user_id: number) => {
    setUserToDelete(user_id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/users/delete`, {
        data: { user_id: userToDelete },
      });
      setData(data.filter((member) => member.user_id !== userToDelete));
    } catch (error) {
      console.error("Error deleting user:", error);
    }

    setDeleteConfirmOpen(false);
    setUserToDelete(null);
    setEditingRow(null);
    setEditData(null);
  };

  const handleEditChange = (field: keyof StaffData, value: any) => {
    if (editData) {
      setEditData({ ...editData, [field]: value });
    }
  };

  // Clean toggle handler
  const handleToggle = async (userId: number, type: "Verified" | "Admin") => {
    const newValue = !switchStates[userId][type];
    setSwitchStates((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [type]: newValue,
      },
    }));

    // Also update editData if we're editing this user
    if (editData && editData.user_id === userId) {
      setEditData({
        ...editData,
        role_data_json: {
          ...editData.role_data_json,
          user_status: {
            ...editData.role_data_json.user_status,
            [type]: newValue ? "Yes" : "No",
          },
        },
      });
    }

    // Trigger API call to update status
    try {
      await axios.put(`${import.meta.env.VITE_API_URL}/users/update-status`, {
        userId: userId,
        status: newValue ? "Yes" : "No",
      });
    } catch (error) {
      console.error(`Error updating ${type} for user ${userId}:`, error);
    }
  };

  // Add handler to close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Effects
  useEffect(() => {
    fetchStaff();
  }, [selectedBrandId]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const columns = useTableColumns(switchStates);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <AddBrandStaff />
      </Box>
      <DynamicTable data={data} columns={columns} onRowClick={handleEdit} />

      <SlidingSidebar
        open={sidebarOpen}
        onClose={handleCloseSidebar}
        width="500px"
      >
        {editData && (
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
              <EditForm
                editData={editData}
                handleEditChange={handleEditChange}
                availableMarkets={availableMarkets}
                availableTags={availableTags}
                switchStates={switchStates}
                handleToggle={handleToggle}
              />
            </Box>
            <ButtonControls>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save Changes
              </Button>
              <Button
                onClick={() => handleDelete(editData.user_id)}
                variant="outlined"
                color="error"
              >
                Delete User
              </Button>
              <Button onClick={handleCloseSidebar} variant="outlined">
                Cancel
              </Button>
            </ButtonControls>
          </Box>
        )}
      </SlidingSidebar>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Changes saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandStaff;
